import Seo from "@components/Seo";
import { graphql, Link, PageProps } from "gatsby";
import React from "react";
import { FlexBox } from "@styles";

import RichText from "@components/RichText";
import Main from "@components/Layout/Main";
import TechData from "@components/ProductsPage/TechData";
import Colors from "@components/ProductsPage/Colors";

import AvailableLines from "@components/ProductsPage/AvailableLines";
import Items from "@components/ProductsPage/Items";
import { ColorLineProduct } from "@services/ColorLines";
import PageSection from "@components/PageSection";

import { GetListImagesFromProduct } from "@services/ProductImages";
import MultiMediaViewer from "@components/MultiMediaViewer";
import PigmentData from "@components/ProductsPage/PigmentData";
import { Brand } from "@typed/categories";
import { GetProductContent } from "@services/ProductContent";
import AdditionalLinks from "@components/ProductsPage/AdditionalLinks";
import { RelatedTopic } from "@typed/relatedTopic";
import RelatedTopicSection from "@components/ProductsPage/RelatedTopics";
// import ColorSystems from "@components/ProductsPage/ColorSystems";

import { useFeatureFlag } from "@hooks/useFeatureFlag";

interface ProductPageContext {
  colorLineDetails: ColorLineProduct | undefined;
  brand?: Brand;
}

const ProductPage = ({
  data: { product_detail, prevProduct, nextProduct },
  pageContext: { brand },
}: PageProps<Queries.ProductPageQuery, ProductPageContext>) => {
  if (!product_detail) return null;

  const {
    products_colors,
    product_name,
    item_parents,
    translations,
    howtoCats,
    brand: { code: brandCode },
    product_groups,
  } = product_detail;

  const enableRelatedTopics = useFeatureFlag("productRelatedTopics") === "true";

  const title = products_colors ? products_colors?.color.color_name : product_name;

  const product_images = GetListImagesFromProduct(product_detail);

  const productContent = GetProductContent(product_detail);

  const relatedTopics = (howtoCats ?? []).reduce((acc, cat) => {
    if (!cat?.howtoCat?.parentCategory || !cat?.howtoCat?.name || !cat?.howtoCat?.slug) return acc;

    acc.push({ name: cat.howtoCat.name, slug: cat.howtoCat.slug, connection: cat.connection, description: cat.description });
    return acc;
  }, [] as RelatedTopic[]);

  const productLines = product_groups?.reduce(
    (acc, pg) => {
      if (!pg?.page_product_group[0]?.title || !pg?.slug || !pg?.brand?.slug) return acc;

      acc.push({ label: pg.page_product_group[0].title, to: `/products/${pg.brand.slug}/${pg.slug}` });
      return acc;
    },
    [] as { label: string; to: string }[],
  );

  return (
    <Main title={title ?? ""} brand={brand} goBack={productLines}>
      <FlexBox gap={2}>
        <FlexBox direction="column" gap={0}>
          <div style={{ flex: "1 0 auto" }}> </div>
          {item_parents.length === 1 ? (
            <div style={{ marginBottom: "1px" }}>
              Item: {item_parents[0].friendly_item_number}
              {item_parents[0].series !== "NA" && ` (Series: ${item_parents[0].series})`}
            </div>
          ) : (
            <>
              Item:
              <ul>
                {item_parents.map((ip) => (
                  <li key={ip.friendly_item_number} style={{ listStyleType: "none" }}>
                    {ip.friendly_item_number} - {ip.description} (Series: {ip.series})
                  </li>
                ))}
              </ul>{" "}
            </>
          )}
        </FlexBox>
      </FlexBox>

      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-2">
        <PageSection sectionName="Product Images" className="md:col-start-2 md:row-span-12 md:row-start-1">
          <MultiMediaViewer files={product_images} />
        </PageSection>

        {translations?.[0]?.description && (
          <PageSection key="ProductDesc" sectionName="Product Description" className="md:col-start-1">
            <RichText body={translations?.[0]?.description} template="no-layout" />
          </PageSection>
        )}

        {product_detail?.products_colors?.color && (prevProduct || nextProduct) && (
          <PageSection className="md:col-start-1" sectionName="Color Navigator">
            <FlexBox key="ColorNav" direction="column">
              {prevProduct && (
                <Link to={`/products/${prevProduct.slug}`} key="prevProduct" className="underline">
                  Prev: {prevProduct?.products_colors?.color?.color_name}
                </Link>
              )}
              {nextProduct && (
                <Link to={`/products/${nextProduct.slug}`} key="nextProduct" className="underline">
                  Next: {nextProduct?.products_colors?.color?.color_name}
                </Link>
              )}
            </FlexBox>
          </PageSection>
        )}

        {product_detail?.products_colors?.color && (
          <div className="col-start-1">
            {/*  Removed until color system data is available  
                    <ColorSystems product={product_detail} /> */}
            <TechData product={product_detail} brand={product_detail?.brand?.code ?? "none"} />
            <PigmentData product={product_detail} />
          </div>
        )}

        {product_detail?.products_noncolor && (
          <div className="col-start-1">
            {" "}
            <TechData product={product_detail} brand={product_detail?.brand?.code ?? "none"} />{" "}
          </div>
        )}

        {product_detail.products_sets && brandCode && (
          <div className="col-start-1">
            <Colors colors={product_detail?.products_sets.constituent_products ?? []} brandCode={brandCode} />{" "}
          </div>
        )}

        {enableRelatedTopics && (relatedTopics?.length ?? 0) > 0 && (
          <div key="relatedTopics" className="col-start-1">
            <RelatedTopicSection relatedTopics={relatedTopics} />
          </div>
        )}

        {productContent.length > 0 && (
          <div className="col-start-1">
            <AdditionalLinks techSheetList={productContent} />{" "}
          </div>
        )}

        {
          // Only show the available lines for Golden Acrylics
          product_detail?.brand.code === "gaa" && <AvailableLines lines={product_detail?.products_colors?.color} />
        }

        {(product_detail.item_parents?.[0]?.items?.length ?? 0) > 0 && <Items itemsFrag={product_detail} />}
      </div>
    </Main>
  );
};

export const Head = ({ data: { product_detail } }: { data: Queries.ProductPageQuery }) => {
  if (!product_detail) return null;

  const product_images = GetListImagesFromProduct(product_detail);

  const translation = product_detail?.translations?.find((t) => t?.language === "en");
  const meta_description = translation?.meta_description ?? translation?.description ?? undefined;

  return (
    <Seo
      title={product_detail?.product_name ?? "Product Page"}
      description={meta_description}
      featuredImageUrl={product_images?.[0]?.fullImageUrl ?? undefined}
    />
  );
};

export default ProductPage;

export const query = graphql`
  query ProductPage($slug: String, $prevProductId: String, $nextProductId: String) {
    product_detail: products(slug: { eq: $slug }) {
      ...ProductDetailPage

      translations {
        description
        meta_description
      }

      product_groups {
        page_product_group {
          title
        }
        brand {
          slug
        }
        slug
      }

      howtoCats {
        connection
        description
        howtoCat {
          name
          slug
          parentCategory
        }
      }
    }

    prevProduct: products(id: { eq: $prevProductId }) {
      id
      product_name
      products_colors {
        color {
          color_name
        }
      }
      slug
    }

    nextProduct: products(id: { eq: $nextProductId }) {
      id
      product_name
      products_colors {
        color {
          color_name
        }
      }
      slug
    }
  }
`;
